// File: /components/Accordions/Accordion.js
"use client";

import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import styles from "./Accordion.module.css";
import CircleCheckbox from "../CircleCheckbox/CircleCheckbox";
import { FaRegCopy, FaArrowUp, FaArrowDown, FaTrash } from "react-icons/fa";

const MARQUEE_GAP = 32;
const AUTO_OPEN_DOWN_RATIO = 0.70;
const AUTO_OPEN_UP_RATIO = 0.30;
const PANEL_TRANSITION_MS = 720;
const PANEL_TRANSITION_TIMEOUT_MS = 920;
const AUTO_SCROLL_LOCK_MS = 860;
const ANCHOR_TRACK_MS = 800;

const AccordionHeaderText = ({ text }) => {
  const wrapperRef = useRef(null);
  const labelRef = useRef(null);
  const [marquee, setMarquee] = useState({ overflow: false, distance: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const wrapper = wrapperRef.current;
      const label = labelRef.current;
      if (!wrapper || !label) return;

      const labelWidth = Math.ceil(label.getBoundingClientRect().width);
      const availableWidth = Math.floor(wrapper.getBoundingClientRect().width);
      const overflow = labelWidth > availableWidth + 1;

      setMarquee((current) => {
        const distance = overflow ? labelWidth + MARQUEE_GAP : 0;
        if (current.overflow === overflow && current.distance === distance) return current;
        return { overflow, distance };
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    if (labelRef.current) observer.observe(labelRef.current);
    document.fonts?.ready?.then(measure).catch(() => {});

    return () => observer.disconnect();
  }, [text]);

  return (
    <div ref={wrapperRef} className={styles.truncatedWrapper}>
      <span
        className={`${styles.truncatedText} ${marquee.overflow ? styles.marqueeActive : ""}`}
        style={{
          "--accordion-marquee-distance": `${marquee.distance}px`,
          "--accordion-marquee-duration": `${Math.max(5, marquee.distance / 45)}s`,
        }}
      >
        <span ref={labelRef}>{text}</span>
        {marquee.overflow && (
          <span aria-hidden="true">{text}</span>
        )}
      </span>
    </div>
  );
};

const Accordion = ({
  items,
  defaultOpenIndex = null,
  defaultOpenIndices = null,
  onOpenIndicesChange,
  collapsible = true,
  showCheckbox = false,
  onCheckboxChange,
  onHeaderClick,
  onLocalReorder,
  onRemoveItem,

  // ✅ Optional: lazy content behavior
  lazyMount = true,
  unmountOnClose = true,

  // ✅ Header controls toggles
  showCopyIcon = true,
  showReorderControls = true,
  showRemoveIcon = true, // only relevant when onRemoveItem exists

  // ✅ NEW: single-open mode (opt-in; safe for existing usage)
  singleOpen = false,

  // ✅ NEW: optionally highlight one accordion header by item.id
  activeItemId = null,

  // Large, live editor panels can keep resizing during a height transition.
  // Opt in to bounded, interruptible transitions without changing accordion UI.
  interruptibleHeightTransitions = false,

  // Optional reading mode: keep one panel open and follow the user's scroll.
  autoOpenOnScroll = false,
}) => {
  const [localItems, setLocalItems] = useState(items || []);
  const [openIndices, setOpenIndices] = useState(() => (
    Array.isArray(defaultOpenIndices)
      ? defaultOpenIndices
      : defaultOpenIndex !== null
        ? [defaultOpenIndex]
        : []
  ));
  const [checkedItems, setCheckedItems] = useState([]);
  const openIndicesRef = useRef(openIndices);

  const [renderedIndices, setRenderedIndices] = useState(() => {
    if (!lazyMount) return new Set();
    if (defaultOpenIndex === null) return new Set();
    return new Set([defaultOpenIndex]);
  });

  const contentRef = useRef([]);
  const innerRef = useRef([]);
  const headerRef = useRef([]);
  const accordionRef = useRef(null);
  const autoScrollLockRef = useRef(0);
  const autoScrollYRef = useRef(0);

  const animatingRef = useRef(new Set());
  const transitionCleanupRef = useRef(new Map());
  const roMap = useRef(new Map());
  const prevOpenSetRef = useRef(new Set(openIndices));

  useEffect(() => {
    openIndicesRef.current = openIndices;
  }, [openIndices]);

  // Sync local items when prop changes
  useEffect(() => {
    setLocalItems(items || []);
  }, [items]);

  // Keep renderedIndices in sync if item count changes
  useEffect(() => {
    if (!lazyMount) return;
    setRenderedIndices((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i >= 0 && i < (items?.length || 0)) next.add(i);
      });
      // Ensure currently-open indices are rendered
      openIndices.forEach((i) => next.add(i));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localItems.length]);

  const nextFrame = () =>
    new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  const attachRO = useCallback((idx) => {
    const panel = contentRef.current[idx];
    const inner = innerRef.current[idx];
    if (!panel || !inner) return;

    roMap.current.get(idx)?.disconnect();

    const ro = new ResizeObserver(() => {
      if (!animatingRef.current.has(idx)) return;
      try {
        const h = inner.getBoundingClientRect().height;
        panel.style.height = `${h}px`;
      } catch {}
    });

    try {
      ro.observe(inner);
    } catch {}
    roMap.current.set(idx, ro);
  }, []);

  const detachRO = useCallback((idx) => {
    roMap.current.get(idx)?.disconnect();
    roMap.current.delete(idx);
  }, []);

  const onTransitionEnd = useCallback(
    (idx, e) => {
      const panel = contentRef.current[idx];
      if (!panel || e?.target !== panel || e?.propertyName !== "height") return;

      // Read the current state, not the state captured when this transition
      // began. Scroll-following can select another panel before the previous
      // animation finishes; using the captured value could leave both panels
      // visually open even though only one is active in React state.
      const openNow = openIndicesRef.current.includes(idx);

      panel.style.height = openNow ? "auto" : "0px";
      animatingRef.current.delete(idx);

      // Unmount content after fully closed
      if (lazyMount && unmountOnClose && !openNow) {
        setRenderedIndices((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      }
    },
    [lazyMount, unmountOnClose]
  );

  const openPanel = useCallback(
    async (idx) => {
      const panel = contentRef.current[idx];
      const inner = innerRef.current[idx];
      if (!panel || !inner) return;
      if (animatingRef.current.has(idx)) return;

      animatingRef.current.add(idx);

      const curH = panel.getBoundingClientRect().height;
      panel.style.height = `${curH}px`;

      const targetH = inner.getBoundingClientRect().height;

      await nextFrame();
      if (!animatingRef.current.has(idx)) return;

      if (panel.style.maxHeight && panel.style.maxHeight !== "none") {
        panel.style.maxHeight = "none";
      }

      let timeoutId = null;
      const cleanup = () => {
        panel.removeEventListener("transitionend", handler);
        if (timeoutId !== null) window.clearTimeout(timeoutId);
        if (transitionCleanupRef.current.get(idx) === cleanup) {
          transitionCleanupRef.current.delete(idx);
        }
      };
      const handler = (e) => {
        if (e?.target !== panel || e?.propertyName !== "height") return;
        cleanup();
        onTransitionEnd(idx, e);
      };
      transitionCleanupRef.current.get(idx)?.();
      transitionCleanupRef.current.set(idx, cleanup);
      panel.addEventListener("transitionend", handler, { once: true });
      // Always recover if the browser skips transitionend (for example, when
      // live content changes the target height during the opening animation).
      timeoutId = window.setTimeout(() => {
        handler({ target: panel, propertyName: "height" });
      }, PANEL_TRANSITION_TIMEOUT_MS);
      panel.style.height = `${targetH}px`;

      attachRO(idx);
    },
    [attachRO, onTransitionEnd]
  );

  const closePanel = useCallback(
    async (idx) => {
      const panel = contentRef.current[idx];
      if (!panel) return;
      if (animatingRef.current.has(idx)) return;

      animatingRef.current.add(idx);

      const curH = panel.getBoundingClientRect().height;
      panel.style.height = `${curH}px`;
      await nextFrame();
      if (!animatingRef.current.has(idx)) return;

      let timeoutId = null;
      const cleanup = () => {
        panel.removeEventListener("transitionend", handler);
        if (timeoutId !== null) window.clearTimeout(timeoutId);
        if (transitionCleanupRef.current.get(idx) === cleanup) {
          transitionCleanupRef.current.delete(idx);
        }
      };
      const handler = (e) => {
        if (e?.target !== panel || e?.propertyName !== "height") return;
        cleanup();
        onTransitionEnd(idx, e);
      };
      transitionCleanupRef.current.get(idx)?.();
      transitionCleanupRef.current.set(idx, cleanup);
      panel.addEventListener("transitionend", handler, { once: true });
      timeoutId = window.setTimeout(() => {
        handler({ target: panel, propertyName: "height" });
      }, PANEL_TRANSITION_TIMEOUT_MS);
      panel.style.height = "0px";

      detachRO(idx);
    },
    [detachRO, onTransitionEnd]
  );

  const toggleAccordion = (index) => {
    // A manual selection changes the document height and may trigger browser
    // scroll anchoring. Give that choice time to settle before scroll-following
    // is allowed to select a different panel.
    if (autoOpenOnScroll) {
      autoScrollLockRef.current = Date.now() + AUTO_SCROLL_LOCK_MS;
    }

    if (animatingRef.current.has(index)) {
      if (!interruptibleHeightTransitions) return;
      transitionCleanupRef.current.get(index)?.();
      animatingRef.current.delete(index);
      detachRO(index);
      const panel = contentRef.current[index];
      if (panel) panel.style.height = `${panel.getBoundingClientRect().height}px`;
    }

    setOpenIndices((prev) => {
      const isOpen = prev.includes(index);
      const opening = !isOpen;

      // If not collapsible and user clicked the currently-open item, do nothing
      if (!collapsible && isOpen) return prev;

      let next;

      if (opening) {
        // ✅ SINGLE OPEN: opening one closes all others
        next = singleOpen ? [index] : [...prev, index];

        // Ensure mounted before opening animation
        if (lazyMount) {
          setRenderedIndices((prevSet) => {
            const s = new Set(prevSet);
            s.add(index);
            return s;
          });
        }
      } else {
        // Closing
        next = prev.filter((i) => i !== index);
      }

      onHeaderClick?.(index);
      onOpenIndicesChange?.(next);
      openIndicesRef.current = next;
      return next;
    });
  };

  const toggleCheckbox = (index) => {
    setCheckedItems((prev) => {
      const next = prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index];
      if (onCheckboxChange) {
        const selected = next.map((i) => localItems[i]?.key ?? i);
        onCheckboxChange(selected);
      }
      return next;
    });
  };

  const handleMoveUp = (index) => {
    if (!showReorderControls) return;
    if (index <= 0) return;
    const newItems = [...localItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setLocalItems(newItems);
    onLocalReorder?.(newItems);
  };

  const handleMoveDown = (index) => {
    if (!showReorderControls) return;
    if (index >= localItems.length - 1) return;
    const newItems = [...localItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setLocalItems(newItems);
    onLocalReorder?.(newItems);
  };

  useEffect(() => {
    localItems.forEach((_, idx) => {
      const panel = contentRef.current[idx];
      if (!panel) return;
      panel.style.overflow = "hidden";
      panel.style.willChange = "height";
      panel.style.height = openIndices.includes(idx) ? "auto" : "0px";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localItems.length]);

  useEffect(() => {
    const prev = prevOpenSetRef.current;
    const next = new Set(openIndices);

    next.forEach((idx) => {
      if (!prev.has(idx)) openPanel(idx);
    });

    prev.forEach((idx) => {
      if (!next.has(idx)) closePanel(idx);
    });

    prevOpenSetRef.current = next;
  }, [openIndices, openPanel, closePanel]);

  useEffect(() => {
    const onResize = () => {
      animatingRef.current.forEach((idx) => {
        const panel = contentRef.current[idx];
        const inner = innerRef.current[idx];
        if (!panel || !inner) return;
        try {
          const h = inner.getBoundingClientRect().height;
          panel.style.height = `${h}px`;
        } catch {}
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => () => {
    transitionCleanupRef.current.forEach((cleanup) => cleanup());
    transitionCleanupRef.current.clear();
  }, []);

  useEffect(() => {
    if (!autoOpenOnScroll || localItems.length === 0) return undefined;

    let animationFrame = 0;
    let anchorFrame = 0;
    let lockReplayTimer = 0;
    let pendingDirection = 0;
    let lastObservedScrollY = window.scrollY;

    const scheduleLockReplay = () => {
      if (lockReplayTimer) return;
      const delay = Math.max(16, autoScrollLockRef.current - Date.now() + 16);
      lockReplayTimer = window.setTimeout(() => {
        lockReplayTimer = 0;
        scheduleUpdate();
      }, delay);
    };

    const updateFromScroll = () => {
      animationFrame = 0;
      const accordion = accordionRef.current;
      if (!accordion) return;
      if (Date.now() < autoScrollLockRef.current) {
        // Height changes and browser scroll anchoring can emit synthetic scroll
        // movement while panels trade places. Treat only separately-recorded
        // user input as intent during this lock.
        autoScrollYRef.current = window.scrollY;
        lastObservedScrollY = window.scrollY;
        if (pendingDirection) scheduleLockReplay();
        return;
      }

      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - autoScrollYRef.current;
      autoScrollYRef.current = currentScrollY;
      const scrollDirection = pendingDirection || Math.sign(scrollDelta);
      pendingDirection = 0;
      if (!scrollDirection) return;

      const currentOpenIndices = openIndicesRef.current;
      const currentIndex = currentOpenIndices[0] ?? 0;
      const accordionRect = accordion.getBoundingClientRect();
      const documentHeight = document.documentElement.scrollHeight;
      const atDocumentBottom = currentScrollY + window.innerHeight >= documentHeight - 2;

      if (accordionRect.top >= window.innerHeight) {
        if (scrollDirection < 0 && currentIndex !== 0) {
          if (lazyMount) {
            setRenderedIndices((rendered) => {
              const next = new Set(rendered);
              next.add(0);
              return next;
            });
          }
          openIndicesRef.current = [0];
          onOpenIndicesChange?.([0]);
          setOpenIndices([0]);
        }
        return;
      }

      if (accordionRect.bottom <= 0) return;

      let nextIndex = currentIndex;

      if (scrollDirection > 0 && currentIndex < localItems.length - 1) {
        const nextHeader = headerRef.current[currentIndex + 1];
        const nextRect = nextHeader?.getBoundingClientRect();
        // Advance one section at a time. At the document boundary the next
        // heading can remain a few pixels below the reading line while the page
        // has no remaining scroll range, so downward input must still advance.
        if (
          nextRect &&
          (nextRect.top <= window.innerHeight * AUTO_OPEN_DOWN_RATIO || atDocumentBottom)
        ) {
          nextIndex = currentIndex + 1;
        }
      } else if (scrollDirection < 0 && currentIndex > 0) {
        const previousHeader = headerRef.current[currentIndex - 1];
        const previousRect = previousHeader?.getBoundingClientRect();
        if (
          previousRect &&
          (previousRect.bottom >= window.innerHeight * AUTO_OPEN_UP_RATIO || currentScrollY <= 1)
        ) {
          nextIndex = currentIndex - 1;
        }
      }

      if (currentOpenIndices.length === 1 && currentOpenIndices[0] === nextIndex) return;

      if (lazyMount) {
        setRenderedIndices((rendered) => {
          const next = new Set(rendered);
          next.add(nextIndex);
          return next;
        });
      }

      const advancing = nextIndex > currentIndex;
      // When moving down, retain the collapsed previous heading as context and
      // bring its bottom edge to the navbar. Moving upward continues to anchor
      // the destination heading in its current reading position.
      const anchorHeader = advancing
        ? headerRef.current[nextIndex - 1]
        : headerRef.current[nextIndex];
      const anchorRect = anchorHeader?.getBoundingClientRect();
      const anchorDocumentTop = anchorRect ? anchorRect.top + window.scrollY : null;
      const anchorViewportBottom = anchorRect?.bottom ?? null;
      autoScrollLockRef.current = Date.now() + AUTO_SCROLL_LOCK_MS;
      onOpenIndicesChange?.([nextIndex]);
      openIndicesRef.current = [nextIndex];
      setOpenIndices([nextIndex]);

      if (anchorHeader && Number.isFinite(anchorDocumentTop)) {
        const anchorStartedAt = performance.now();
        let previousDocumentTop = anchorDocumentTop;
        const preserveHeaderPosition = () => {
          const currentRect = anchorHeader.getBoundingClientRect();
          const elapsed = performance.now() - anchorStartedAt;

          if (advancing && Number.isFinite(anchorViewportBottom)) {
            const nav = document.querySelector('nav');
            const measuredNavBottom = nav?.getBoundingClientRect().bottom;
            const cssNavOffset = parseFloat(
              getComputedStyle(document.documentElement).getPropertyValue('--nav-offset')
            );
            const navBottom = Number.isFinite(measuredNavBottom) && measuredNavBottom > 0
              ? measuredNavBottom
              : Number.isFinite(cssNavOffset)
                ? cssNavOffset
                : 40;
            const progress = Math.min(1, elapsed / ANCHOR_TRACK_MS);
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const desiredBottom = anchorViewportBottom
              + (navBottom - anchorViewportBottom) * easedProgress;
            const positionError = currentRect.bottom - desiredBottom;
            if (Math.abs(positionError) > 0.5) window.scrollBy(0, positionError);
          } else {
            // Compensate only for the heading moving within the document as
            // the panels animate. Document coordinates separate layout shift
            // from the user's own wheel/touch scrolling.
            const currentDocumentTop = currentRect.top + window.scrollY;
            const layoutShift = currentDocumentTop - previousDocumentTop;
            if (Math.abs(layoutShift) > 0.5) window.scrollBy(0, layoutShift);
            previousDocumentTop = currentDocumentTop;
          }
          autoScrollYRef.current = window.scrollY;
          lastObservedScrollY = window.scrollY;

          if (elapsed < ANCHOR_TRACK_MS) {
            anchorFrame = window.requestAnimationFrame(preserveHeaderPosition);
          }
        };
        anchorFrame = window.requestAnimationFrame(preserveHeaderPosition);
      }
    };

    const scheduleUpdate = () => {
      const observedScrollY = window.scrollY;
      const observedDelta = observedScrollY - lastObservedScrollY;
      lastObservedScrollY = observedScrollY;

      if (Date.now() < autoScrollLockRef.current) {
        autoScrollYRef.current = observedScrollY;
        if (pendingDirection) scheduleLockReplay();
        return;
      }
      if (Math.abs(observedDelta) >= 2) pendingDirection = Math.sign(observedDelta);
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updateFromScroll);
    };

    const recordWheelIntent = (event) => {
      if (Math.abs(event.deltaY) < 1) return;
      pendingDirection = Math.sign(event.deltaY);
      // A wheel gesture at the top or bottom does not emit a scroll event.
      // Schedule directly so boundary sections can still become active.
      if (Date.now() < autoScrollLockRef.current) scheduleLockReplay();
      else scheduleUpdate();
    };

    autoScrollYRef.current = window.scrollY;
    lastObservedScrollY = window.scrollY;
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.addEventListener('wheel', recordWheelIntent, { passive: true });

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      if (anchorFrame) window.cancelAnimationFrame(anchorFrame);
      if (lockReplayTimer) window.clearTimeout(lockReplayTimer);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('wheel', recordWheelIntent);
    };
  }, [autoOpenOnScroll, lazyMount, localItems.length, onOpenIndicesChange]);

  const renderItemContent = (item) => {
    if (typeof item.content === "function") return item.content();
    return item.content;
  };

  return (
    <div
      ref={accordionRef}
      className={styles.accordion}
      style={{ "--accordion-panel-transition": `${PANEL_TRANSITION_MS}ms` }}
    >
      {localItems.map((item, index) => {
        const isOpen = openIndices.includes(index);
        const shouldRenderContent = !lazyMount || renderedIndices.has(index);

        const isActiveItem =
          activeItemId !== null &&
          typeof activeItemId !== "undefined" &&
          Number(item.id) === Number(activeItemId);

        return (
          <div key={item.key ?? item.id ?? index}>
            <div
              ref={(el) => (headerRef.current[index] = el)}
              className={`${styles.accordionHeader} acc-header ${isOpen ? styles.active : ""}`}
              style={
                isActiveItem
                  ? {
                      border: "1px solid #7d7d00",
                    }
                  : undefined
              }
              onClick={(e) => {
                e.stopPropagation();
                toggleAccordion(index);
              }}
              data-target={`#content_${index}`}
            >
              <span className={styles.headerText}>
                {typeof item.header === "string" ? (
                  <AccordionHeaderText text={item.header} />
                ) : item.header}
              </span>

              {/* Copy icon (optional) */}
              {showCopyIcon && (
                <span
                  className={styles.copyIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof item.header === "string") {
                      navigator.clipboard.writeText(item.header);
                    }
                  }}
                  title="Copy title"
                >
                  <FaRegCopy />
                </span>
              )}

              {/* Right controls (optional) */}
              <span className={styles.rightControls} onClick={(e) => e.stopPropagation()}>
                {showReorderControls && (
                  <span className={styles.moveArrows}>
                    <FaArrowUp onClick={() => handleMoveUp(index)} />
                    <FaArrowDown onClick={() => handleMoveDown(index)} />
                    {showRemoveIcon && onRemoveItem && (
                      <FaTrash
                        style={{ marginLeft: "8px", cursor: "pointer" }}
                        color="#ff6b6b"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveItem(item);
                        }}
                        title="Remove"
                      />
                    )}
                  </span>
                )}

                {showCheckbox && (
                  <span className={styles.checkboxWrapper}>
                    <CircleCheckbox
                      color="green"
                      checked={checkedItems.includes(index)}
                      onChange={() => toggleCheckbox(index)}
                    />
                  </span>
                )}

                {/* If reorder controls are hidden but remove is desired, allow remove alone */}
                {!showReorderControls && showRemoveIcon && onRemoveItem && (
                  <span className={styles.moveArrows}>
                    <FaTrash
                      style={{ marginLeft: "8px", cursor: "pointer" }}
                      color="#ff6b6b"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveItem(item);
                      }}
                      title="Remove"
                    />
                  </span>
                )}
              </span>
            </div>

            <div
              ref={(el) => (contentRef.current[index] = el)}
              className={`${styles.accordionContent} eq-acc-panel acc-panel`}
            >
              <div
                ref={(el) => (innerRef.current[index] = el)}
                className={`${styles.accordionInterior} ${item.safe ? styles.listingSafe : ""}`}
              >
                {shouldRenderContent ? renderItemContent(item) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
